import React, { useEffect, useState, memo } from 'react';
import { GlassesIcon, SmartphoneIcon, MonitorIcon, CloudIcon, BuildingIcon, ChevronRightIcon, PlayCircleIcon, CheckCircleIcon, ArrowRightIcon, ZapIcon, ShieldIcon, UsersIcon, TrendingUpIcon, MessageSquareIcon, SettingsIcon, FileIcon, VideoIcon, MicIcon, BrainIcon, LockIcon, BarChart3Icon, CodeIcon, PackageIcon, GitBranchIcon, TerminalIcon, BookOpenIcon, XIcon, PlugIcon, SmartphoneIcon as AndroidIcon, LayersIcon, SparklesIcon } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { SiteHeader } from '../components/SiteHeader';
import { SiteFooter } from '../components/SiteFooter';
import { scrollToSection } from '../utils/scroll';
export function AriOSMarketing() {
  const location = useLocation();
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [mousePosition, setMousePosition] = useState({
    x: 0,
    y: 0
  });
  // Set page title
  useEffect(() => {
    document.title = 'AR Operating System';
  }, []);
  // Track mouse position for parallax effects
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth - 0.5) * 20,
        y: (e.clientY / window.innerHeight - 0.5) * 20
      });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);
  // Scroll to the requested section when arriving from another page
  useEffect(() => {
    const target = (location.state as {
      scrollTo?: string;
    } | null)?.scrollTo;
    if (!target) return;
    const timeout = window.setTimeout(() => scrollToSection(target), 100);
    return () => window.clearTimeout(timeout);
  }, [location.state]);
  return <div className="w-full min-h-screen bg-black relative overflow-hidden" style={{
    zoom: '0.9'
  }} data-id="element-125" data-mp="v6xntc3zr1ed">
      {/* Enhanced Animated Background with Apple-style depth */}
      <div className="fixed inset-0 z-0" data-id="element-126" data-mp="s49w2e2znl6m">
        {/* Base gradient with smooth transitions */}
        <div className="absolute inset-0 bg-gradient-to-br from-zinc-950 via-black to-zinc-900 transition-all duration-1000" data-id="element-127" data-mp="o3r2oflg672b"></div>

        {/* Animated gradient orbs with parallax */}
        <div className="absolute inset-0 opacity-30 transition-all duration-700 ease-out" style={{
        transform: `translate(${mousePosition.x * 0.5}px, ${mousePosition.y * 0.5}px)`
      }} data-id="element-128" data-mp="x1telq6pyk4d">
          <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-gradient-radial from-[#E9A165]/20 via-[#E9A165]/5 to-transparent rounded-full blur-3xl animate-pulse-slow" data-id="element-129" data-mp="16yxytkh085ij"></div>
          <div className="absolute top-1/3 right-1/4 w-[500px] h-[500px] bg-gradient-radial from-[#8846DE]/20 via-[#8846DE]/5 to-transparent rounded-full blur-3xl animate-pulse-slow" style={{
          animationDelay: '1s'
        }} data-id="element-130" data-mp="1fu484tfhh5pt"></div>
          <div className="absolute bottom-1/4 left-1/3 w-[550px] h-[550px] bg-gradient-radial from-[#D25796]/20 via-[#D25796]/5 to-transparent rounded-full blur-3xl animate-pulse-slow" style={{
          animationDelay: '2s'
        }} data-id="element-131" data-mp="29emvl5t9ekyk"></div>
        </div>

        {/* Glossy mesh gradient overlay */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(233,161,101,0.12),transparent_50%),radial-gradient(ellipse_at_bottom_right,rgba(136,70,222,0.12),transparent_50%),radial-gradient(ellipse_at_bottom_left,rgba(210,87,150,0.12),transparent_50%)] opacity-60" data-id="element-132" data-mp="1ppclq8g72u8d"></div>

        {/* Subtle animated grid */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:100px_100px] opacity-20 animate-grid-flow" data-id="element-133" data-mp="1lmy39pgftwhm"></div>

        {/* Glass reflection effect */}
        <div className="absolute inset-0 bg-gradient-to-b from-white/[0.02] via-transparent to-transparent" data-id="element-134" data-mp="2f0h6acahefw5"></div>
      </div>

      <SiteHeader data-id="element-135" data-mp="2a14cf7l708k2" />

      {/* Main Content - All sections in one flow */}
      <div className="relative z-10" data-id="element-136" data-mp="25ae71ucro5pd">
        {/* Hero Section */}
        <section id="home" className="min-h-screen flex items-center justify-center pt-20 px-6" data-id="element-137" data-mp="258ky28eol9e7">
          <div className="max-w-7xl mx-auto text-center" data-id="element-138" data-mp="1vchucy46l2p5">
            <h2 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white mt-12 mb-8 leading-tight" data-id="element-139" data-mp="1ve1beqxnvhrz">
              Voice-First OS for the
              <br data-id="element-140" data-mp="1mbjpyeptyt61" />
              <span className="bg-gradient-to-r from-[#E9A165] via-[#8846DE] to-[#D25796] bg-clip-text text-transparent animate-gradient" data-id="element-141" data-mp="1mb0ad4nnkgv6">
                Next Generation of Devices
              </span>
            </h2>

            <p className="text-xl md:text-2xl text-zinc-400 mb-12 max-w-4xl mx-auto leading-relaxed" data-id="element-142" data-mp="3goi1t2r6hxvh">
              Work hands-free across all your devices with AI-powered voice
              control and intelligent cloud sync
            </p>

            {/* CTA Buttons - Updated to scroll instead of navigate */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6" data-id="element-143" data-mp="3o7iwqznwyj78">
              <a href="https://chat.ari-os.com" target="_blank" rel="noopener noreferrer" className="px-10 py-5 bg-gradient-to-r from-[#E9A165] via-[#8846DE] to-[#D25796] text-white rounded-xl hover:shadow-2xl hover:shadow-[#8846DE]/50 transition-all font-bold text-lg group relative overflow-hidden" data-id="element-144" data-mp="3t0u4r44gp60q">
                <span className="relative z-10" data-id="element-145" data-mp="3a8rlrm4cldnb">Try Ari Now</span>
                <div className="absolute inset-0 bg-gradient-to-r from-[#D25796] via-[#8846DE] to-[#E9A165] opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-146" data-mp="3f96v82lie100"></div>
              </a>
              <button onClick={() => scrollToSection('architecture')} className="px-10 py-5 bg-white/5 backdrop-blur-xl border-2 border-white/20 text-white rounded-xl hover:bg-white/10 hover:border-white/40 transition-all font-bold text-lg" data-id="element-147" data-mp="uier1m34skti">
                Ecosystem Architecture
              </button>
            </div>

            {/* Early Access CTA */}
            <div className="flex justify-center mb-16" data-id="element-148" data-mp="2vkxjllhrtg27">
              <a href="https://forms.cloud.microsoft/pages/responsepage.aspx?id=xMS4P2WNLUSAk22HkPxrhRXI1sYi83xFmdd8cOAxygVUM040Wk1XUFo2R0M5S0ZZMkFNVllCSDVYVi4u" target="_blank" rel="noopener noreferrer" className="group inline-flex items-center gap-3 px-6 py-3 rounded-full bg-white/[0.04] backdrop-blur-xl border border-[#E9A165]/30 hover:border-[#E9A165]/60 hover:bg-white/[0.08] transition-all" data-id="element-149" data-mp="2vlfmh0yxusj3">
                <span className="px-2.5 py-1 rounded-full bg-[#E9A165]/15 border border-[#E9A165]/30 text-[#E9A165] text-xs font-semibold uppercase tracking-wider" data-id="element-150" data-mp="1o5rp8qe8hr4n">
                  Early Access
                </span>
                <span className="text-white font-semibold text-base" data-id="element-151" data-mp="1a0987ana1nw8">
                  Sign up for Ari OS on the RealWear Navigator Z1
                </span>
                <ArrowRightIcon size={18} className="text-[#E9A165] group-hover:translate-x-1 transition-transform" data-id="element-152" data-mp="1045wpe6jccwm" />
              </a>
            </div>

            {/* Video Placeholder */}
            <div className="relative max-w-6xl mx-auto" data-id="element-153" data-mp="1sstkbgerbedc">
              <div className="absolute -inset-4 bg-gradient-to-r from-[#E9A165] via-[#8846DE] to-[#D25796] rounded-3xl opacity-20 blur-2xl" data-id="element-154" data-mp="1xrep7t4yem84"></div>
            </div>
          </div>
        </section>

        {/* Ari OS Section */}
        <section id="ari-os" className="min-h-screen flex items-center py-32 px-6" data-id="element-155" data-mp="1nre2rybsfpzp">
          <div className="max-w-7xl mx-auto" data-id="element-156" data-mp="7de6qyg2ckhq">
            <div className="text-center mb-16" data-id="element-157" data-mp="cebml4dlp2g2">
              <div className="inline-block mb-6" data-id="element-158" data-mp="cf3ikgb4126v">
                <div className="px-6 py-2 bg-gradient-to-r from-[#E9A165]/20 to-[#D25796]/20 backdrop-blur-xl border border-zinc-800 rounded-full" data-id="element-159" data-mp="3pt6perec3rug">
                  <img src="https://cdn.magicpatterns.com/uploads/3vCu7oKxcfkEiVUzGfZBCk/Ari_Logo_White.png" alt="Ari OS" className="h-4 inline-block" data-id="element-160" data-mp="3ks98sj8z35v2" />
                </div>
              </div>
              <h2 className="text-5xl md:text-6xl font-bold text-white mb-6" data-id="element-161" data-mp="ccfpjwn9bgab">
                Ari on Device
              </h2>
              <p className="text-xl text-zinc-400 max-w-3xl mx-auto" data-id="element-162" data-mp="7ffro90k9jua">
                Control your entire device with just your voice, hands-free
                productivity at its finest
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-24" data-id="element-163" data-mp="v48jpue2b7jm">
              <div data-id="element-164" data-mp="v636zfct3bqv">
                <h3 className="text-4xl font-bold text-white mb-6" data-id="element-165" data-mp="qds4c4qolbum">
                  Voice-First Interface
                </h3>
                <p className="text-zinc-400 mb-10 text-lg leading-relaxed" data-id="element-166" data-mp="291bid3bed601">
                  Control everything with natural voice commands. Ari
                  understands context and responds intelligently to help you
                  stay productive.
                </p>
                <div className="space-y-6" data-id="element-167" data-mp="2n0kotb9swlqt">
                  <div className="flex items-start space-x-4 group" data-id="element-168" data-mp="2n1ciggju89eg">
                    <div className="w-14 h-14 bg-gradient-to-br from-[#E9A165] to-[#D25796] rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform shadow-xl" data-id="element-169" data-mp="2ry2zb63gv90x">
                      <MicIcon size={24} className="text-white" data-id="element-170" data-mp="31q8ok82c3ccf" />
                    </div>
                    <div data-id="element-171" data-mp="35phgr8hq725v">
                      <h4 className="text-white font-bold text-lg mb-2" data-id="element-172" data-mp="35pr932q8tleq">
                        Natural Language Processing
                      </h4>
                      <p className="text-zinc-500" data-id="element-173" data-mp="3fo7075p9suo8">
                        Speak naturally, Ari understands intent and context
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4 group" data-id="element-174" data-mp="2plumy82b4fw7">
                    <div className="w-14 h-14 bg-gradient-to-br from-[#E9A165] to-[#D25796] rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform shadow-xl" data-id="element-175" data-mp="2pjzxqhk061mb">
                      <BrainIcon size={24} className="text-white" data-id="element-176" data-mp="33dr97vhanvko" />
                    </div>
                    <div data-id="element-177" data-mp="33g570c3czlvx">
                      <h4 className="text-white font-bold text-lg mb-2" data-id="element-178" data-mp="38cc9tw0gkh9n">
                        Contextual Awareness
                      </h4>
                      <p className="text-zinc-500" data-id="element-179" data-mp="3dcq8wok30gvj">
                        Remembers previous conversations and preferences
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4 group" data-id="element-180" data-mp="1izqomi4rayhx">
                    <div className="w-14 h-14 bg-gradient-to-br from-[#E9A165] to-[#D25796] rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform shadow-xl" data-id="element-181" data-mp="1n0kc5aeaebaa">
                      <ZapIcon size={24} className="text-white" data-id="element-182" data-mp="1aohx8r2hn083" />
                    </div>
                    <div data-id="element-183" data-mp="15r8choq1ncql">
                      <h4 className="text-white font-bold text-lg mb-2" data-id="element-184" data-mp="15qgey8cvz4xo">
                        Instant Response
                      </h4>
                      <p className="text-zinc-500" data-id="element-185" data-mp="1yem18zjl91zm">
                        Lightning-fast processing for real-time assistance
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="relative" data-id="element-186" data-mp="1pcuudrdbap46">
                <div className="absolute -inset-4 bg-gradient-to-r from-[#E9A165] to-[#D25796] rounded-3xl opacity-20 blur-2xl" data-id="element-187" data-mp="1pg0mzgl5g94n"></div>
                <div className="relative aspect-video bg-gradient-to-br from-zinc-900/50 to-zinc-950/50 backdrop-blur-xl rounded-2xl border-2 border-zinc-800 flex items-center justify-center overflow-hidden" data-id="element-188" data-mp="dzalubvl27hw">
                  <img src="https://cdn.magicpatterns.com/uploads/7sJmhbQwXp2rt8FjRa293u/devices_new_3.png" alt="Ari OS Devices Ecosystem" className="w-full h-full object-cover" data-id="element-189" data-mp="ai4pvctb3ngr" />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-190" data-mp="5gfcvrmiwzq1">
              <div className="order-2 lg:order-1 relative" data-id="element-191" data-mp="3nuyc21gkrzoe">
                <div className="absolute -inset-4 bg-gradient-to-r from-[#E9A165] to-[#D25796] rounded-3xl opacity-20 blur-2xl" data-id="element-192" data-mp="3smputpthp13z"></div>
                <div className="relative aspect-video bg-gradient-to-br from-zinc-900/50 to-zinc-950/50 backdrop-blur-xl rounded-2xl border-2 border-zinc-800 flex items-center justify-center overflow-hidden" data-id="element-193" data-mp="3dyv0y63qwqpb">
                  <img src="https://cdn.magicpatterns.com/uploads/nT6uLZCb3k3Aij25WepRHP/person_talking_2.png" alt="Hands-Free Control Demo - Person using AR glasses with voice commands" className="w-full h-full object-cover" data-id="element-194" data-mp="34yge32b4v3jp" />
                </div>
              </div>
              <div className="order-1 lg:order-2" data-id="element-195" data-mp="2ldog2d729d46">
                <h3 className="text-4xl font-bold text-white mb-6" data-id="element-196" data-mp="2zbwc8iw9waea">
                  Complete Voice Control
                </h3>
                <p className="text-zinc-400 mb-10 text-lg leading-relaxed" data-id="element-197" data-mp="2vbm6968hdjzf">
                  Take pictures, make calls, reply to emails, send messages, and
                  control your entire device - all hands-free with just your
                  voice.
                </p>
                <div className="space-y-6" data-id="element-198" data-mp="3iykkdx94ofu6">
                  <div className="flex items-start space-x-4 group" data-id="element-199" data-mp="3e01001a6nc3x">
                    <div className="w-14 h-14 bg-gradient-to-br from-[#E9A165] to-[#D25796] rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform shadow-xl" data-id="element-200" data-mp="3e23wn45uc3hm">
                      <GlassesIcon size={24} className="text-white" data-id="element-201" data-mp="3mymdvj4pm2yd" />
                    </div>
                    <div data-id="element-202" data-mp="1l9ffbon5xdgt">
                      <h4 className="text-white font-bold text-lg mb-2" data-id="element-203" data-mp="1qacv5ukp9vf5">
                        Hands-Free Photography
                      </h4>
                      <p className="text-zinc-500" data-id="element-204" data-mp="1v2mj55298les">
                        Take pictures and videos without touching your device
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4 group" data-id="element-205" data-mp="12e8isinp1d02">
                    <div className="w-14 h-14 bg-gradient-to-br from-[#E9A165] to-[#D25796] rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform shadow-xl" data-id="element-206" data-mp="17ayy33roc4kf">
                      <VideoIcon size={24} className="text-white" data-id="element-207" data-mp="1gdhwnboffv0v" />
                    </div>
                    <div data-id="element-208" data-mp="2mtaf82p2n0ll">
                      <h4 className="text-white font-bold text-lg mb-2" data-id="element-209" data-mp="2mrzb21rprqty">
                        Microsoft Teams & Calls
                      </h4>
                      <p className="text-zinc-500" data-id="element-210" data-mp="31v97iwfs1f25">
                        Make calls on Teams and other platforms with voice
                        commands
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4 group" data-id="element-211" data-mp="2s4o1inxrx897">
                    <div className="w-14 h-14 bg-gradient-to-br from-[#E9A165] to-[#D25796] rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform shadow-xl" data-id="element-212" data-mp="2o4dwbdht2lve">
                      <MessageSquareIcon size={24} className="text-white" data-id="element-213" data-mp="2e5etwcnmvmgu" />
                    </div>
                    <div data-id="element-214" data-mp="296br6yv4lsc1">
                      <h4 className="text-white font-bold text-lg mb-2" data-id="element-215" data-mp="297ms8qx6slzg">
                        Email & Messaging
                      </h4>
                      <p className="text-zinc-500" data-id="element-216" data-mp="257maoxesjuo6">
                        Reply to emails and send messages using only your voice
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4 group" data-id="element-217" data-mp="1wwm21evbtvxg">
                    <div className="w-14 h-14 bg-gradient-to-br from-[#E9A165] to-[#D25796] rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform shadow-xl" data-id="element-218" data-mp="1wvay9f1vso6c">
                      <MicIcon size={24} className="text-white" data-id="element-219" data-mp="1ntunv7b2gm24" />
                    </div>
                    <div data-id="element-220" data-mp="1rxslfdkqtot2">
                      <h4 className="text-white font-bold text-lg mb-2" data-id="element-221" data-mp="2fn3pp51ay1by">
                        Talk to Ari
                      </h4>
                      <p className="text-zinc-500" data-id="element-222" data-mp="2kl595r2n42ny">
                        Ask questions and get instant answers from your AI
                        assistant
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Web Portal Section */}
        <section id="chat" className="min-h-screen flex items-center py-32 px-6 bg-gradient-to-b from-black to-zinc-950" data-id="element-223" data-mp="106d1jrakmg6j">
          <div className="max-w-7xl mx-auto" data-id="element-224" data-mp="ro1ainr5vwsp">
            <div className="text-center mb-16" data-id="element-225" data-mp="nnr81l2kvoil">
              <div className="inline-block mb-6" data-id="element-226" data-mp="nmz8y09rv8nk">
                <div className="px-6 py-2 bg-gradient-to-r from-[#D25796]/20 to-[#E9A165]/20 backdrop-blur-xl border border-zinc-800 rounded-full" data-id="element-227" data-mp="11niduyixxyli">
                  <span className="text-sm font-semibold bg-gradient-to-r from-[#D25796] to-[#E9A165] bg-clip-text text-transparent" data-id="element-228" data-mp="wpqn47u18iiw">
                    WEB
                  </span>
                </div>
              </div>
              <h2 className="text-5xl md:text-6xl font-bold text-white mb-6" data-id="element-229" data-mp="41l01efiahfw">
                Conversational Interface
              </h2>
              <p className="text-xl text-zinc-400 max-w-3xl mx-auto" data-id="element-230" data-mp="dy7k4100d4gv">
                Talk to Ari from anywhere - your computer, headset, or mobile
                device
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-16" data-id="element-231" data-mp="3hf6byx9oxx5w">
              <div className="relative" data-id="element-232" data-mp="2vvlb9sgra0hr">
                <div className="absolute -inset-4 bg-gradient-to-r from-[#D25796] to-[#E9A165] rounded-3xl opacity-20 blur-2xl" data-id="element-233" data-mp="2vwmvamfr1xbm"></div>
                <div className="relative aspect-video bg-gradient-to-br from-zinc-900/50 to-zinc-950/50 backdrop-blur-xl rounded-2xl border-2 border-zinc-800 flex items-center justify-center overflow-hidden" data-id="element-234" data-mp="30w9ah1f51nn2">
                  <img src="https://cdn.magicpatterns.com/uploads/6F5gyPGn14eCsFN6uaP8Ti/image.png" alt="Ari Chat Interface - Voice-Powered Assistant" className="w-full h-full object-cover" data-id="element-235" data-mp="3eu7b740jdlk3" />
                </div>
              </div>
              <div data-id="element-236" data-mp="3tiu1u1vm5zqm">
                <h3 className="text-4xl font-bold text-white mb-6" data-id="element-237" data-mp="3tjdccxixplum">
                  Voice-Powered Assistant
                </h3>
                <p className="text-zinc-400 mb-10 text-lg leading-relaxed" data-id="element-238" data-mp="2en9r23d0o6f">
                  Use your voice to talk to Ari and get help with emails,
                  messages, calendar, and any questions you have. Chat from your
                  computer, headset, or mobile device.
                </p>
                <div className="space-y-4" data-id="element-239" data-mp="1mtjozi0jlwyk">
                  <div className="flex items-start space-x-3" data-id="element-240" data-mp="1hv8dl2ulnee8">
                    <div className="w-6 h-6 rounded-full bg-[#D25796]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-241" data-mp="1hxuoncgp8c17">
                      <CheckCircleIcon size={14} className="text-[#D25796]" data-id="element-242" data-mp="2ak5ub1j5x34v" />
                    </div>
                    <p className="text-zinc-400" data-id="element-243" data-mp="25kt01vpyots9">
                      Help with mail, messages, and calendar
                    </p>
                  </div>
                  <div className="flex items-start space-x-3" data-id="element-244" data-mp="20sje0qrywdv9">
                    <div className="w-6 h-6 rounded-full bg-[#D25796]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-245" data-mp="1vuhxodlxesnh">
                      <CheckCircleIcon size={14} className="text-[#D25796]" data-id="element-246" data-mp="2tc88m68c07by" />
                    </div>
                    <p className="text-zinc-400" data-id="element-247" data-mp="2oi3wl8k7l940">
                      Answer any question instantly
                    </p>
                  </div>
                  <div className="flex items-start space-x-3" data-id="element-248" data-mp="3182raqa1cvyf">
                    <div className="w-6 h-6 rounded-full bg-[#D25796]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-249" data-mp="31aoxoml6x5f2">
                      <CheckCircleIcon size={14} className="text-[#D25796]" data-id="element-250" data-mp="2s860oj435n0q" />
                    </div>
                    <p className="text-zinc-400" data-id="element-251" data-mp="3oy6i1x24l180">
                      Chat from computer, headset, or mobile
                    </p>
                  </div>
                  <div className="flex items-start space-x-3" data-id="element-252" data-mp="3k16bzm59zygw">
                    <div className="w-6 h-6 rounded-full bg-[#D25796]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-253" data-mp="3f6smu1h7inko">
                      <CheckCircleIcon size={14} className="text-[#D25796]" data-id="element-254" data-mp="3b4fjtktuh3rl" />
                    </div>
                    <p className="text-zinc-400" data-id="element-255" data-mp="bkv34lreksx7">
                      Supports all major languages
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Features Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-256" data-mp="1udd2rm1ncssm">
              <div className="bg-gradient-to-br from-zinc-900/50 to-zinc-950/50 backdrop-blur-xl rounded-2xl p-6 border border-zinc-800 hover:scale-105 transition-transform duration-300" data-id="element-257" data-mp="1yfzxbuppcrti">
                <div className="w-12 h-12 bg-gradient-to-br from-[#D25796] to-[#E9A165] rounded-xl flex items-center justify-center mb-4" data-id="element-258" data-mp="28dxl6h3ngmll">
                  <MessageSquareIcon size={24} className="text-white" data-id="element-259" data-mp="2dbz6z9sgn00r" />
                </div>
                <h4 className="text-white font-bold text-lg mb-2" data-id="element-260" data-mp="2i4s7tmkt5o7k">
                  Conversation History
                </h4>
                <p className="text-zinc-500 text-sm" data-id="element-261" data-mp="2m4j6sdkhnan6">
                  View and search through all your past conversations with Ari
                </p>
              </div>

              <div className="bg-gradient-to-br from-zinc-900/50 to-zinc-950/50 backdrop-blur-xl rounded-2xl p-6 border border-zinc-800 hover:scale-105 transition-transform duration-300" data-id="element-262" data-mp="2w4jsg65ty2uj">
                <div className="w-12 h-12 bg-gradient-to-br from-[#D25796] to-[#E9A165] rounded-xl flex items-center justify-center mb-4" data-id="element-263" data-mp="r2xmn8a1piwv">
                  <GlassesIcon size={24} className="text-white" data-id="element-264" data-mp="15sdbgiq0ne9x" />
                </div>
                <h4 className="text-white font-bold text-lg mb-2" data-id="element-265" data-mp="10yr7rg1tykq9">
                  Switch Between Devices
                </h4>
                <p className="text-zinc-500 text-sm" data-id="element-266" data-mp="1etl70t15e10b">
                  Seamlessly switch chats between headset and web interface
                </p>
              </div>

              <div className="bg-gradient-to-br from-zinc-900/50 to-zinc-950/50 backdrop-blur-xl rounded-2xl p-6 border border-zinc-800 hover:scale-105 transition-transform duration-300" data-id="element-267" data-mp="1opei07z7xmfz">
                <div className="w-12 h-12 bg-gradient-to-br from-[#D25796] to-[#E9A165] rounded-xl flex items-center justify-center mb-4" data-id="element-268" data-mp="3qi2oqrb2ms0i">
                  <ZapIcon size={24} className="text-white" data-id="element-269" data-mp="3lkjb9tm76j9m" />
                </div>
                <h4 className="text-white font-bold text-lg mb-2" data-id="element-270" data-mp="210xktr81t3c2">
                  All Ari Skills
                </h4>
                <p className="text-zinc-500 text-sm" data-id="element-271" data-mp="1racgro9lv6lr">
                  Access all of Ari's capabilities from the chat interface
                </p>
              </div>

              <div className="bg-gradient-to-br from-zinc-900/50 to-zinc-950/50 backdrop-blur-xl rounded-2xl p-6 border border-zinc-800 hover:scale-105 transition-transform duration-300" data-id="element-272" data-mp="1w7mersb28ql7">
                <div className="w-12 h-12 bg-gradient-to-br from-[#D25796] to-[#E9A165] rounded-xl flex items-center justify-center mb-4" data-id="element-273" data-mp="1nac2q4qf4xf8">
                  <UsersIcon size={24} className="text-white" data-id="element-274" data-mp="1dal8831uyif5" />
                </div>
                <h4 className="text-white font-bold text-lg mb-2" data-id="element-275" data-mp="1dcny8n30gbjv">
                  Manage Profile
                </h4>
                <p className="text-zinc-500 text-sm" data-id="element-276" data-mp="37ocdkqd5mg4s">
                  Update your profile and subscription status anytime
                </p>
              </div>

              <div className="bg-gradient-to-br from-zinc-900/50 to-zinc-950/50 backdrop-blur-xl rounded-2xl p-6 border border-zinc-800 hover:scale-105 transition-transform duration-300" data-id="element-277" data-mp="17ljv0cmgl1kh">
                <div className="w-12 h-12 bg-gradient-to-br from-[#D25796] to-[#E9A165] rounded-xl flex items-center justify-center mb-4" data-id="element-278" data-mp="yk3ls87dr5hu">
                  <SettingsIcon size={24} className="text-white" data-id="element-279" data-mp="onh1plmvoigv" />
                </div>
                <h4 className="text-white font-bold text-lg mb-2" data-id="element-280" data-mp="opbpd7pjaoon">
                  Activate Skills
                </h4>
                <p className="text-zinc-500 text-sm" data-id="element-281" data-mp="fux7cxdmd01u">
                  Enable or disable Ari skills based on your needs
                </p>
              </div>

              <div className="bg-gradient-to-br from-zinc-900/50 to-zinc-950/50 backdrop-blur-xl rounded-2xl p-6 border border-zinc-800 hover:scale-105 transition-transform duration-300" data-id="element-282" data-mp="atzhdymdb1ps">
                <div className="w-12 h-12 bg-gradient-to-br from-[#D25796] to-[#E9A165] rounded-xl flex items-center justify-center mb-4" data-id="element-283" data-mp="yodkbchzewt">
                  <PlugIcon size={24} className="text-white" data-id="element-284" data-mp="3ry6p7rulpjwp" />
                </div>
                <h4 className="text-white font-bold text-lg mb-2" data-id="element-285" data-mp="3s09pr5tdqvfo">
                  Manage Integrations
                </h4>
                <p className="text-zinc-500 text-sm" data-id="element-286" data-mp="5rrzkg0mbcuy">
                  Add or remove integrations with third-party services
                </p>
              </div>
            </div>

            <div className="mt-12 text-center" data-id="element-287" data-mp="397yspsleprph">
              <a href="https://chat.ari-os.com" target="_blank" rel="noopener noreferrer" className="inline-block px-10 py-5 bg-gradient-to-r from-[#D25796] to-[#E9A165] text-white rounded-xl hover:shadow-2xl hover:shadow-[#D25796]/50 transition-all font-bold text-lg" data-id="element-288" data-mp="398qnx2b3dnf8">
                Start Chatting
              </a>
            </div>
          </div>
        </section>

        {/* Cloud Section */}
        <section id="cloud" className="min-h-screen flex items-center py-32 px-6" data-id="element-289" data-mp="2ifzwb1zxnwyb">
          <div className="max-w-7xl mx-auto" data-id="element-290" data-mp="2dgn92g55gnv7">
            <div className="text-center mb-16" data-id="element-291" data-mp="2dfuaw3k9b6oo">
              <div className="inline-block mb-6" data-id="element-292" data-mp="2s5jhieb4c2wc">
                <div className="px-6 py-2 bg-gradient-to-r from-[#8846DE]/20 to-[#E9A165]/20 backdrop-blur-xl border border-zinc-800 rounded-full" data-id="element-293" data-mp="2s8fiycm6d9pj">
                  <span className="text-sm font-semibold bg-gradient-to-r from-[#8846DE] to-[#E9A165] bg-clip-text text-transparent" data-id="element-294" data-mp="1ziyt9mpxw6x0">
                    CLOUD
                  </span>
                </div>
              </div>
              <h2 className="text-5xl md:text-6xl font-bold text-white mb-6" data-id="element-295" data-mp="29hekrqsvpi71">
                Your Personal Hub
              </h2>
              <p className="text-xl text-zinc-400 max-w-3xl mx-auto" data-id="element-296" data-mp="3kv8iirntw2xy">
                AI-powered cloud storage that remembers everything and keeps you
                in control
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start mb-16" data-id="element-297" data-mp="3nf58ka4o9cp6">
              <div data-id="element-298" data-mp="3sehtuqfwabpn">
                <h3 className="text-4xl font-bold text-white mb-6" data-id="element-299" data-mp="3sf1c689gj247">
                  Intelligent Storage
                </h3>
                <p className="text-zinc-400 mb-10 text-lg leading-relaxed" data-id="element-300" data-mp="3egkuxndyxt7t">
                  Your personal AI hub that stores files, remembers context, and
                  keeps everything in sync across all your devices.
                </p>

                {/* AI Memory */}
                <div className="mb-8" data-id="element-301" data-mp="eytkw3m542lf">
                  <div className="flex items-center space-x-3 mb-4" data-id="element-302" data-mp="f1zadjyol6ho">
                    <div className="w-10 h-10 bg-gradient-to-br from-[#8846DE] to-[#E9A165] rounded-lg flex items-center justify-center" data-id="element-303" data-mp="17ovduchkqot">
                      <BrainIcon size={20} className="text-white" data-id="element-304" data-mp="57p8nbnumvua" />
                    </div>
                    <h4 className="text-white font-bold text-xl" data-id="element-305" data-mp="sxjlvuc2ymf2">AI Memory</h4>
                  </div>
                  <div className="space-y-2 ml-13" data-id="element-306" data-mp="xs7adt733gtn">
                    <div className="flex items-start space-x-3" data-id="element-307" data-mp="90ru5rq6i8r2">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-308" data-mp="499vjabmok99">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-309" data-mp="3vgcjzu030cfs" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-310" data-mp="3qhj5pzcz0hek">
                        Remembers your preferences and habits
                      </p>
                    </div>
                    <div className="flex items-start space-x-3" data-id="element-311" data-mp="myycu0mk4265">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-312" data-mp="mxn89ylaeqdz">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-313" data-mp="d1tmdolohkjj" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-314" data-mp="32qvvv8jhvhpd">
                        Recalls previous conversations
                      </p>
                    </div>
                    <div className="flex items-start space-x-3" data-id="element-315" data-mp="1d9i8tytr9rha">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-316" data-mp="1db309fkrwuax">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-317" data-mp="1n7o808o6gjig" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-318" data-mp="1ry4lttvwu75c">
                        View and edit what AI stores about you
                      </p>
                    </div>
                  </div>
                </div>

                {/* Files & Knowledge */}
                <div className="mb-8" data-id="element-319" data-mp="20x9kd07h20hi">
                  <div className="flex items-center space-x-3 mb-4" data-id="element-320" data-mp="25yfe91cvbtsm">
                    <div className="w-10 h-10 bg-gradient-to-br from-[#8846DE] to-[#E9A165] rounded-lg flex items-center justify-center" data-id="element-321" data-mp="25w2x2qexpngm">
                      <FileIcon size={20} className="text-white" data-id="element-322" data-mp="bko666a6thza" />
                    </div>
                    <h4 className="text-white font-bold text-xl" data-id="element-323" data-mp="gizfmfwklqgz">
                      Files & Knowledge
                    </h4>
                  </div>
                  <div className="space-y-2 ml-13" data-id="element-324" data-mp="23q37sbgy2p7">
                    <div className="flex items-start space-x-3" data-id="element-325" data-mp="6w9jix1jjbxp">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-326" data-mp="6xklqsfc8bmp">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-327" data-mp="3tasvyo6ygh1j" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-328" data-mp="kv97e2h8bmst">
                        Upload documents and notes
                      </p>
                    </div>
                    <div className="flex items-start space-x-3" data-id="element-329" data-mp="ps9bw8yfkhht">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-330" data-mp="awtbznb65rdi">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-331" data-mp="fy90jpck2g0n" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-332" data-mp="30mnau0bzoq16">
                        Ask AI questions about your files
                      </p>
                    </div>
                    <div className="flex items-start space-x-3" data-id="element-333" data-mp="1in76g2zwqtnz">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-334" data-mp="1dovvfoxvmd46">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-335" data-mp="1rox6fx7uv0xd" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-336" data-mp="1nm0j5t7degn3">
                        Import from Google Drive, OneDrive, Dropbox
                      </p>
                    </div>
                  </div>
                </div>

                {/* Devices */}
                <div data-id="element-337" data-mp="18rw0oa6h8wsd">
                  <div className="flex items-center space-x-3 mb-4" data-id="element-338" data-mp="18sf5qqb13qoz">
                    <div className="w-10 h-10 bg-gradient-to-br from-[#8846DE] to-[#E9A165] rounded-lg flex items-center justify-center" data-id="element-339" data-mp="13zvs5tcccvk6">
                      <SmartphoneIcon size={20} className="text-white" data-id="element-340" data-mp="g8qgzrhcirg7" />
                    </div>
                    <h4 className="text-white font-bold text-xl" data-id="element-341" data-mp="c8pqv9kvwrtb">
                      Device Management
                    </h4>
                  </div>
                  <div className="space-y-2 ml-13" data-id="element-342" data-mp="2gcavxi9gq7ru">
                    <div className="flex items-start space-x-3" data-id="element-343" data-mp="2gapynkvrkaqa">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-344" data-mp="2l4u6e6cusmsf">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-345" data-mp="2q4ow46kw76bw" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-346" data-mp="1sh83l0bg9m6m">
                        See all connected devices
                      </p>
                    </div>
                    <div className="flex items-start space-x-3" data-id="element-347" data-mp="22c8f7zcw5ke6">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-348" data-mp="27cnq8k9pafsz">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-349" data-mp="3drevt19rqotc" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-350" data-mp="3ipq6fe7w13cm">
                        Control sync frequency
                      </p>
                    </div>
                    <div className="flex items-start space-x-3" data-id="element-351" data-mp="124bebst1m034">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-352" data-mp="126xvmk5qjtyj">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-353" data-mp="sfjrfz2ua5z2" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-354" data-mp="nggnyj2ic7t7">
                        Remotely disconnect devices
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div data-id="element-355" data-mp="eg1yd7iig6jw">
                {/* Media */}
                <div className="mb-8" data-id="element-356" data-mp="9g5vxcpgon6g">
                  <div className="flex items-center space-x-3 mb-4" data-id="element-357" data-mp="9h7ee28t4by7">
                    <div className="w-10 h-10 bg-gradient-to-br from-[#8846DE] to-[#E9A165] rounded-lg flex items-center justify-center" data-id="element-358" data-mp="28ttg6uaztwqf">
                      <VideoIcon size={20} className="text-white" data-id="element-359" data-mp="23uq9l5fd7igc" />
                    </div>
                    <h4 className="text-white font-bold text-xl" data-id="element-360" data-mp="11i3s5rx3n7tn">Media</h4>
                  </div>
                  <div className="space-y-2 ml-13" data-id="element-361" data-mp="15h2wbab0b0k1">
                    <div className="flex items-start space-x-3" data-id="element-362" data-mp="rilbbkchk25t">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-363" data-mp="riczlvs3n2w7">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-364" data-mp="hstfkmsnh2zr" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-365" data-mp="mj9te80duqmn">
                        Auto-saves photos/videos from devices
                      </p>
                    </div>
                    <div className="flex items-start space-x-3" data-id="element-366" data-mp="8q0p2lrakvaz">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-367" data-mp="drzr9sbapm6c">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-368" data-mp="3v1ejfhmhvysl" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-369" data-mp="3uv17hebx6k9">
                        View media timeline anytime
                      </p>
                    </div>
                  </div>
                </div>

                {/* Skills & Integrations */}
                <div className="mb-8" data-id="element-370" data-mp="3qblo59b3zlky">
                  <div className="flex items-center space-x-3 mb-4" data-id="element-371" data-mp="3q8fynsykihop">
                    <div className="w-10 h-10 bg-gradient-to-br from-[#8846DE] to-[#E9A165] rounded-lg flex items-center justify-center" data-id="element-372" data-mp="3la4p7jc6q970">
                      <SettingsIcon size={20} className="text-white" data-id="element-373" data-mp="3v9vhrfh6qe4g" />
                    </div>
                    <h4 className="text-white font-bold text-xl" data-id="element-374" data-mp="cvwdro1snz22">
                      Skills & Integrations
                    </h4>
                  </div>
                  <div className="space-y-2 ml-13" data-id="element-375" data-mp="7xcqnkbgsh84">
                    <div className="flex items-start space-x-3" data-id="element-376" data-mp="mttzo4w6ljri">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-377" data-mp="mrzecpgzzpmw">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-378" data-mp="oygy2ysb4ya5" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-379" data-mp="twqrp4ydvqtn">
                        Add or remove Ari skills
                      </p>
                    </div>
                    <div className="flex items-start space-x-3" data-id="element-380" data-mp="yua562n9bzkj">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-381" data-mp="13nv725rz1dp0">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-382" data-mp="18o95qx7onbad" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-383" data-mp="1cpmbz8eotght">
                        Manage third-party integrations
                      </p>
                    </div>
                    <div className="flex items-start space-x-3" data-id="element-384" data-mp="1mkmjpwd0cuk3">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-385" data-mp="3jer3cbyj6iao">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-386" data-mp="3oe3uva0ckdjl" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-387" data-mp="3gvclmohmjd7q">
                        Customize skill settings
                      </p>
                    </div>
                  </div>
                </div>

                <div className="relative" data-id="element-388" data-mp="3749n9ug2xcc5">
                  <div className="absolute -inset-4 bg-gradient-to-r from-[#8846DE] to-[#E9A165] rounded-3xl opacity-20 blur-2xl" data-id="element-389" data-mp="3255ayuxuyyhl"></div>
                  <div className="relative bg-gradient-to-br from-zinc-900/50 to-zinc-950/50 backdrop-blur-xl rounded-2xl border-2 border-zinc-800 overflow-hidden" data-id="element-390" data-mp="3lyitev2aw7b">
                    <img src="https://cdn.magicpatterns.com/uploads/nPnXM4vnWRjVHqvZQRMm5A/image.png" alt="Ari Cloud Dashboard - Account, Memory, Chat History, Media Collection" className="w-full h-auto max-h-[600px] object-cover object-top" data-id="element-391" data-mp="3ptyd5952q2kn" />
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center" data-id="element-392" data-mp="2z43up17xg3dt">
              <a href="https://cloud.ari-os.com" target="_blank" rel="noopener noreferrer" className="inline-block px-10 py-5 bg-gradient-to-r from-[#8846DE] to-[#E9A165] text-white rounded-xl hover:shadow-2xl hover:shadow-[#8846DE]/50 transition-all font-bold text-lg" data-id="element-393" data-mp="2z32b28cui8kh">
                Explore User Cloud
              </a>
            </div>
          </div>
        </section>

        {/* Business Section */}
        <section id="business" className="min-h-screen flex items-center py-32 px-6 bg-gradient-to-b from-black to-[#0a0a0a]" data-id="element-394" data-mp="3mtg3w1398bg4">
          <div className="max-w-7xl mx-auto" data-id="element-395" data-mp="3d3mqcujjxm98">
            <div className="text-center mb-16" data-id="element-396" data-mp="3d2boj09o2okr">
              <div className="inline-block mb-6" data-id="element-397" data-mp="4ntb0j0yigtr">
                <div className="px-6 py-2 bg-gradient-to-r from-[#D25796]/20 to-[#E9A165]/20 backdrop-blur-xl border border-white/10 rounded-full" data-id="element-398" data-mp="4n1p5zx5vxgo">
                  <span className="text-sm font-semibold bg-gradient-to-r from-[#D25796] to-[#E9A165] bg-clip-text text-transparent" data-id="element-399" data-mp="1u4ykrzqnuzq4">
                    BUSINESS
                  </span>
                </div>
              </div>
              <h2 className="text-5xl md:text-6xl font-bold text-white mb-6" data-id="element-400" data-mp="23tyn9t4dgd92">
                Enterprise Control
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto" data-id="element-401" data-mp="1yy9uq9qrrwm9">
                Fleet management and analytics for organizations
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-402" data-mp="168t49hmpmpso">
              <div className="relative" data-id="element-403" data-mp="1l5tnsxuqzeg2">
                <div className="absolute -inset-4 bg-gradient-to-r from-[#D25796] to-[#E9A165] rounded-3xl opacity-20 blur-2xl" data-id="element-404" data-mp="1l2pdrjulpohh"></div>
                <div className="relative aspect-video bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-xl rounded-2xl border-2 border-white/10 flex items-center justify-center overflow-hidden" data-id="element-405" data-mp="2wjp6ggkv25c1">
                  <img src="https://cdn.magicpatterns.com/uploads/iRVkz4jdtoc25hx5UZzgBd/business_cloud.png" alt="Ari Business Cloud - Enterprise Dashboard with Device Status, Firmware, and Reporting" className="w-full h-full object-cover" data-id="element-406" data-mp="m85bv2lwa6r3" />
                </div>
              </div>
              <div data-id="element-407" data-mp="r1i03bmnwbjb">
                <h3 className="text-4xl font-bold text-white mb-6" data-id="element-408" data-mp="39t3hj2t50wk">
                  Enterprise-Grade Management
                </h3>
                <p className="text-gray-300 mb-10 text-lg leading-relaxed" data-id="element-409" data-mp="3uh5lg0bilidj">
                  Manage fleets of AR devices, deploy updates, monitor usage,
                  and maintain security policies at scale.
                </p>
                <div className="space-y-6 mb-10" data-id="element-410" data-mp="7a38asev5l9u">
                  <div className="flex items-start space-x-4 group" data-id="element-411" data-mp="1np4pwqp8cvns">
                    <div className="w-14 h-14 bg-gradient-to-br from-[#D25796] to-[#E9A165] rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform shadow-xl" data-id="element-412" data-mp="1nrh4quzoypwm">
                      <UsersIcon size={24} className="text-white" data-id="element-413" data-mp="1v971r8pnwxp6" />
                    </div>
                    <div data-id="element-414" data-mp="1v6uceahkrl23">
                      <h4 className="text-white font-bold text-lg mb-2" data-id="element-415" data-mp="1h9xig065tngz">
                        Fleet Management
                      </h4>
                      <p className="text-gray-400" data-id="element-416" data-mp="1m90nhkm53toy">
                        Centralized control of all devices
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4 group" data-id="element-417" data-mp="2evuylqx2hgpr">
                    <div className="w-14 h-14 bg-gradient-to-br from-[#D25796] to-[#E9A165] rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform shadow-xl" data-id="element-418" data-mp="208s0pj4u161w">
                      <LockIcon size={24} className="text-white" data-id="element-419" data-mp="2swnsqajgi9sc" />
                    </div>
                    <div data-id="element-420" data-mp="2xo5xl9olouil">
                      <h4 className="text-white font-bold text-lg mb-2" data-id="element-421" data-mp="2xofl8qm6apl4">
                        Security Policies
                      </h4>
                      <p className="text-gray-400" data-id="element-422" data-mp="37ha3dvoakzvl">
                        Enterprise-grade security controls
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4 group" data-id="element-423" data-mp="2xnjit1vad9l6">
                    <div className="w-14 h-14 bg-gradient-to-br from-[#D25796] to-[#E9A165] rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform shadow-xl" data-id="element-424" data-mp="2tj3ev658gc8l">
                      <TrendingUpIcon size={24} className="text-white" data-id="element-425" data-mp="2jsh4bqcdibwr" />
                    </div>
                    <div data-id="element-426" data-mp="2evibdb4g4b05">
                      <h4 className="text-white font-bold text-lg mb-2" data-id="element-427" data-mp="2esw1h4u31jer">
                        Analytics & Insights
                      </h4>
                      <p className="text-gray-400" data-id="element-428" data-mp="25uk6kqm6nzgi">
                        Usage metrics and performance data
                      </p>
                    </div>
                  </div>
                </div>
                <a href="https://business.ari-os.com" target="_blank" rel="noopener noreferrer" className="inline-block px-10 py-5 bg-gradient-to-r from-[#D25796] to-[#E9A165] text-white rounded-xl hover:shadow-2xl hover:shadow-[#D25796]/50 transition-all font-bold text-lg" data-id="element-429" data-mp="gc3qp3fk9wpv">
                  Enterprise Solutions
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Mobile App Section */}
        <section id="mobile-app" className="min-h-screen flex items-center py-32 px-6" data-id="element-430" data-mp="1oiodbqig1j0">
          <div className="max-w-7xl mx-auto" data-id="element-431" data-mp="6l96s5psf97l">
            <div className="text-center mb-16" data-id="element-432" data-mp="bebxm636k4k3">
              <div className="inline-block mb-6" data-id="element-433" data-mp="bg58jxkttaxw">
                <div className="px-6 py-2 bg-gradient-to-r from-[#8846DE]/20 to-[#D25796]/20 backdrop-blur-xl border border-zinc-800 rounded-full" data-id="element-434" data-mp="3a4u6z3u6qlfw">
                  <span className="text-sm font-semibold bg-gradient-to-r from-[#8846DE] to-[#D25796] bg-clip-text text-transparent" data-id="element-435" data-mp="3f12mwjg7iio4">
                    MOBILE APP
                  </span>
                </div>
              </div>
              <h2 className="text-5xl md:text-6xl font-bold text-white mb-6" data-id="element-436" data-mp="1ud9woygbrgdn">
                Ari Mobile
              </h2>
              <p className="text-xl text-zinc-400 max-w-3xl mx-auto" data-id="element-437" data-mp="1pkyvztdrfqk1">
                Your AR device companion for iOS and Android - setup, control,
                and manage everything
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start mb-16" data-id="element-438" data-mp="1zbbihr54xtst">
              <div data-id="element-439" data-mp="2dab0ify57ag0">
                <h3 className="text-4xl font-bold text-white mb-6" data-id="element-440" data-mp="2d9i5gc8jq9dp">
                  Easy Setup & Control
                </h3>
                <p className="text-zinc-400 mb-10 text-lg leading-relaxed" data-id="element-441" data-mp="ixl5tvzy9x7k">
                  Get started in seconds with automatic device discovery and
                  one-tap pairing. Manage all your AR devices from one place.
                </p>

                {/* Easy Setup */}
                <div className="mb-8" data-id="element-442" data-mp="4ufa93dymmgy">
                  <div className="flex items-center space-x-3 mb-4" data-id="element-443" data-mp="8twi23guzvpa">
                    <div className="w-10 h-10 bg-gradient-to-br from-[#8846DE] to-[#D25796] rounded-lg flex items-center justify-center" data-id="element-444" data-mp="3r7nmf4p511yf">
                      <ZapIcon size={20} className="text-white" data-id="element-445" data-mp="b2wj0s8cfc" />
                    </div>
                    <h4 className="text-white font-bold text-xl" data-id="element-446" data-mp="3h8esucy6ppcl">Easy Setup</h4>
                  </div>
                  <div className="space-y-2 ml-13" data-id="element-447" data-mp="3m9lyo6groegm">
                    <div className="flex items-start space-x-3" data-id="element-448" data-mp="3m66mpcie3ljf">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-449" data-mp="38acl7ejy3ygc">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-450" data-mp="3d9pfulh269th" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-451" data-mp="1bghq0i3tk9tc">
                        Automatically finds AR devices nearby
                      </p>
                    </div>
                    <div className="flex items-start space-x-3" data-id="element-452" data-mp="18zqewm2pzmcm">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-453" data-mp="14z6m1up6j4wa">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-454" data-mp="10391tdaspee8" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-455" data-mp="v465ch8vsghv">
                        One-tap pairing with your device
                      </p>
                    </div>
                    <div className="flex items-start space-x-3" data-id="element-456" data-mp="1ns255utqyoiv">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-457" data-mp="1izh90mndbzc6">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-458" data-mp="1e04m61whys9l" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-459" data-mp="2bhwcxxz7lmw9">
                        Connect to Bluetooth headphones
                      </p>
                    </div>
                    <div className="flex items-start space-x-3" data-id="element-460" data-mp="2e1jbtpj9ujg7">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-461" data-mp="2izujpuq0ajvs">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-462" data-mp="2nynxlo97gcwh" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-463" data-mp="2sz1vwel088hb">
                        Quickly send Wi-Fi login details
                      </p>
                    </div>
                  </div>
                </div>

                {/* Device Controls */}
                <div className="mb-8" data-id="element-464" data-mp="31wc8q4dh05oc">
                  <div className="flex items-center space-x-3 mb-4" data-id="element-465" data-mp="36p5d2r511bzw">
                    <div className="w-10 h-10 bg-gradient-to-br from-[#8846DE] to-[#D25796] rounded-lg flex items-center justify-center" data-id="element-466" data-mp="3boi7py253nd1">
                      <SettingsIcon size={20} className="text-white" data-id="element-467" data-mp="3gomjxbw0lvxg" />
                    </div>
                    <h4 className="text-white font-bold text-xl" data-id="element-468" data-mp="3kq7zj4zxoobc">
                      Device Controls
                    </h4>
                  </div>
                  <div className="space-y-2 ml-13" data-id="element-469" data-mp="9wlf4ahd0d4m">
                    <div className="flex items-start space-x-3" data-id="element-470" data-mp="5sdq0c6zov5v">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-471" data-mp="jsombur2j1yf">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-472" data-mp="ev56iqeq2h4d" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-473" data-mp="3na6pvpzt5mt1">
                        Manage multiple headsets from one phone
                      </p>
                    </div>
                    <div className="flex items-start space-x-3" data-id="element-474" data-mp="uc65p1qortc">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-475" data-mp="3s8qcztq514mz">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-476" data-mp="34idvps905vjq" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-477" data-mp="2zj1cdhhcb6lw">
                        Adjust volume, brightness, and settings
                      </p>
                    </div>
                    <div className="flex items-start space-x-3" data-id="element-478" data-mp="1jta5miisq5nf">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-479" data-mp="1jutn2cg6umqs">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-480" data-mp="1a2filbxq720x" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-481" data-mp="1xrzzlpabjp7k">
                        Manage profile and subscription status
                      </p>
                    </div>
                  </div>
                </div>

                {/* Media & Chat */}
                <div data-id="element-482" data-mp="1otxtivbqa6df">
                  <div className="flex items-center space-x-3 mb-4" data-id="element-483" data-mp="1tqwpzkiv6pes">
                    <div className="w-10 h-10 bg-gradient-to-br from-[#8846DE] to-[#D25796] rounded-lg flex items-center justify-center" data-id="element-484" data-mp="1tt11anj3eqp3">
                      <MessageSquareIcon size={20} className="text-white" data-id="element-485" data-mp="icapmovwh6o3" />
                    </div>
                    <h4 className="text-white font-bold text-xl" data-id="element-486" data-mp="3ckf28782h2sw">
                      Media & Chat
                    </h4>
                  </div>
                  <div className="space-y-2 ml-13" data-id="element-487" data-mp="3hh5iovnsa0eu">
                    <div className="flex items-start space-x-3" data-id="element-488" data-mp="3mhju8nolfshm">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-489" data-mp="3mg8owjfi2coe">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-490" data-mp="2ttxn55g5q5q0" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-491" data-mp="2yrphs786s5xw">
                        View and delete photos/videos from device
                      </p>
                    </div>
                    <div className="flex items-start space-x-3" data-id="element-492" data-mp="37kkcw83xwl9y">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-493" data-mp="37m3wo8ot1egh">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-494" data-mp="2f49mm6fsvb3c" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-495" data-mp="27liiu0fuj2yv">
                        Talk to Ari on your phone
                      </p>
                    </div>
                    <div className="flex items-start space-x-3" data-id="element-496" data-mp="22jt4obxbu96k">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-497" data-mp="1yj95l2t70vhs">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-498" data-mp="1tnbt57l1w9aa" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-499" data-mp="1onz6ony3d488">
                        View Ari conversation history
                      </p>
                    </div>
                    <div className="flex items-start space-x-3" data-id="element-500" data-mp="1ex4kfjkui4ms">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-501" data-mp="1av9jig6ttt9j">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-502" data-mp="15ubt5gbnxswy" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-503" data-mp="10x23q0o9xh94">
                        Add and remove Ari integrations
                      </p>
                    </div>
                    <div className="flex items-start space-x-3" data-id="element-504" data-mp="w8au0qrj58xg">
                      <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-505" data-mp="w5of262b7r57">
                        <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-506" data-mp="m91sncuc4s12" />
                      </div>
                      <p className="text-zinc-500 text-sm" data-id="element-507" data-mp="1a0gyhflblrh2">
                        Customize profile and themes
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="relative flex items-center justify-center" data-id="element-508" data-mp="109323goo17s5">
                {/* Glow Effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-[#8846DE] to-[#D25796] rounded-[3rem] opacity-20 blur-3xl" data-id="element-509" data-mp="156mhijx3nqlo"></div>

                {/* Phone Mockup */}
                <div className="relative w-[340px] h-[700px]" data-id="element-510" data-mp="1xps2r71vq3zy">
                  {/* Phone Frame */}
                  <div className="absolute inset-0 bg-gradient-to-b from-zinc-700 to-zinc-800 rounded-[3rem] shadow-2xl border-[3px] border-zinc-600/50" data-id="element-511" data-mp="3ie47dys7dcjp">
                    {/* Inner bezel */}
                    <div className="absolute inset-[10px] bg-zinc-900 rounded-[2.4rem]" data-id="element-512" data-mp="3s3xasci6ylcv">
                      {/* Screen */}
                      <div className="absolute inset-[3px] rounded-[2.2rem] overflow-hidden" data-id="element-513" data-mp="3n88hus0og2pj">
                        <img src="https://cdn.magicpatterns.com/uploads/x2mGyh8RJbMReKTSfHvhJC/Screen_Andorid.jpg" alt="Ari Companion App - Device management screen showing Arc3-3813 connected with battery, display, volume controls and quick actions" className="w-full h-full object-cover" data-id="element-514" data-mp="2zgt8wgeean5b" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="text-center" data-id="element-515" data-mp="1ezliwaaesrt1">
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-516" data-mp="1f0wvmzlu19wa">
                <a href="https://play.google.com/store/apps/details?id=com.ari_os.companion&hl=en" target="_blank" rel="noopener noreferrer" className="inline-flex items-center space-x-3 px-10 py-5 bg-white/5 backdrop-blur-xl border-2 border-white/20 text-white rounded-xl hover:bg-white/10 hover:border-white/40 transition-all font-bold text-lg" data-id="element-517" data-mp="1j28aqb72ycq8">
                  <img src="https://cdn.magicpatterns.com/uploads/grMQzHgPVptgUWdveugAA9/163-1632345_download-the-domus-app-today-android-icon-white.png" alt="Android" className="w-6 h-6" data-id="element-518" data-mp="21xyeo3ykpj6r" />
                  <span data-id="element-519" data-mp="2gq8nx5xk4zd8">Download for Android</span>
                </a>
                <a href="https://apps.apple.com/ch/app/ari-companion/id6747385775" target="_blank" rel="noopener noreferrer" className="inline-flex items-center space-x-3 px-10 py-5 bg-white/5 backdrop-blur-xl border-2 border-white/20 text-white rounded-xl hover:bg-white/10 hover:border-white/40 transition-all font-bold text-lg" data-id="element-520" data-mp="2lnionhqecxgd">
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor" data-id="element-521" data-mp="3r9vdlxlj3t8h">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" data-id="element-522" data-mp="nsd7pvpbgwap" />
                  </svg>
                  <span data-id="element-523" data-mp="30z1oi2xzngnf">Download for iOS</span>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Architecture Overview Section */}
        <section id="architecture" className="py-32 px-6" data-id="element-524" data-mp="35zpeclahdrg2">
          <div className="max-w-7xl mx-auto" data-id="element-525" data-mp="2r870at16m6zc">
            <div className="text-center mb-16" data-id="element-526" data-mp="2w835uwpj26h0">
              <div className="inline-block mb-6" data-id="element-527" data-mp="2w6s3907tj4rh">
                <div className="px-6 py-2 bg-gradient-to-r from-[#E9A165]/20 via-[#8846DE]/20 to-[#D25796]/20 backdrop-blur-xl border border-white/10 rounded-full" data-id="element-528" data-mp="2i8bkgawolnsz">
                  <span className="text-sm font-semibold bg-gradient-to-r from-[#E9A165] via-[#8846DE] to-[#D25796] bg-clip-text text-transparent" data-id="element-529" data-mp="2mb88wi8wke4u">
                    ARCHITECTURE
                  </span>
                </div>
              </div>
              <h2 className="text-5xl md:text-6xl font-bold text-white mb-6" data-id="element-530" data-mp="6j0wdcrpo9b6">
                Ecosystem Overview
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto" data-id="element-531" data-mp="bgj635djysk8">
                A unified platform connecting devices, apps, and cloud services
              </p>
            </div>

            {/* Architecture Grid */}
            <div className="relative max-w-6xl mx-auto" data-id="element-532" data-mp="1r2mqpq8ze9e0">
              {/* Connection Lines Layer - Hidden on mobile */}
              <div className="hidden lg:block absolute inset-0 pointer-events-none" data-id="element-533" data-mp="25riuvc7x329c">
                {/* Companion App to Supported Devices connection (Violet) - Centered vertically */}
                <div className="absolute top-[140px] left-[50%] w-[33.33%] h-1 bg-[#8846DE] shadow-[0_0_15px_rgba(136,70,222,0.5)]" data-id="element-534" data-mp="2esh3k7tavxy9"></div>

                {/* Ari User Cloud to Business Cloud connection (Red) - Horizontal line between bottom bubbles */}
                <div className="absolute top-[560px] left-[25%] w-[50%] h-1 bg-[#f64f59] shadow-[0_0_15px_rgba(246,79,89,0.5)]" data-id="element-535" data-mp="2jqin0tun1za9"></div>

                {/* Orange connecting lines - Web Portal to Supported Devices to both Clouds */}
                {/* Horizontal line connecting Web Portal to Supported Devices (bottom of top row cards) - shortened */}
                <div className="absolute top-[360px] left-[12.5%] w-[75%] h-1 bg-[#E9A165] shadow-[0_0_15px_rgba(233,161,101,0.5)]" data-id="element-536" data-mp="3gg15wtqy7fy"></div>

                {/* Vertical line from horizontal orange line down to Ari User Cloud */}
                <div className="absolute top-[360px] left-[22.5%] w-1 h-[200px] bg-[#E9A165] shadow-[0_0_15px_rgba(233,161,101,0.5)]" data-id="element-537" data-mp="i7om3smpf4mr"></div>

                {/* Vertical line from horizontal orange line down to Business Cloud - centered with Business Cloud bubble */}
                <div className="absolute top-[360px] left-[77.5%] w-1 h-[200px] bg-[#E9A165] shadow-[0_0_15px_rgba(233,161,101,0.5)]" data-id="element-538" data-mp="3lpqdfl7rz83b"></div>

                {/* Vertical line coming down from Web Portal - centered with the card */}
                <div className="absolute top-[280px] left-[12.5%] w-1 h-[84px] bg-[#E9A165] shadow-[0_0_15px_rgba(233,161,101,0.5)]" data-id="element-539" data-mp="15xamgjetbpwc"></div>

                {/* Vertical line coming down from Supported Devices - centered with the card */}
                <div className="absolute top-[280px] left-[87.5%] w-1 h-[84px] bg-[#E9A165] shadow-[0_0_15px_rgba(233,161,101,0.5)]" data-id="element-540" data-mp="2ufvmijpn1qro"></div>
              </div>

              {/* Top Row - 3 Cards */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 lg:gap-32 mb-32 relative z-10" data-id="element-541" data-mp="2lmileuhf7iui">
                {/* Web Portal */}
                <button onClick={() => scrollToSection('chat')} className="group bg-white/[0.05] backdrop-blur-[40px] rounded-[32px] p-8 border-2 border-white/20 shadow-[0_20px_60px_-20px_rgba(253,160,133,0.4),0_0_0_1px_rgba(253,160,133,0.1)] transition-all duration-400 hover:-translate-y-3 hover:scale-[1.05] hover:border-[#fda085]/40 hover:shadow-[0_30px_80px_-20px_rgba(253,160,133,0.6),0_0_40px_rgba(253,160,133,0.3)] relative overflow-hidden cursor-pointer" data-id="element-542" data-mp="2bpvzs3h9snrf">
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(253,160,133,0.15),transparent_60%)] opacity-0 group-hover:opacity-100 transition-opacity duration-400" data-id="element-543" data-mp="3s3m6zj3ors5a"></div>
                  <div className="absolute -inset-[1px] bg-gradient-to-b from-[#fda085]/20 to-transparent rounded-[32px] opacity-0 group-hover:opacity-100 transition-opacity duration-400" data-id="element-544" data-mp="k9pvs5u69oly"></div>
                  <div className="relative flex flex-col items-center text-center" data-id="element-545" data-mp="akprq80tc30w">
                    <div className="w-16 h-16 rounded-[20px] bg-gradient-to-br from-[#fda085]/20 to-[#fda085]/5 flex items-center justify-center mb-5 shadow-[inset_0_0_20px_rgba(253,160,133,0.1)] border border-[#fda085]/20 group-hover:shadow-[0_0_30px_rgba(253,160,133,0.4)] transition-all duration-400" data-id="element-546" data-mp="5ld7lv1bta20">
                      <MonitorIcon size={28} className="text-[#fda085] group-hover:scale-110 transition-transform duration-400" data-id="element-547" data-mp="3nr8228uaqnk9" />
                    </div>
                    <h3 className="text-xl font-bold mb-2 tracking-tight text-white" data-id="element-548" data-mp="1lzbsv5vlppsn">
                      Ari Web
                    </h3>
                    <p className="text-xs uppercase tracking-[1.5px] font-semibold text-white/60 mb-4" data-id="element-549" data-mp="1tiuk1zf00pcb">
                      Desktop Interface
                    </p>
                    <div className="text-sm text-white/70 leading-relaxed flex items-center justify-center gap-2 flex-wrap" data-id="element-550" data-mp="239ywosh7by3z">
                      <span data-id="element-551" data-mp="2878ryor9v3zq">• Full chat interface</span>
                      <span data-id="element-552" data-mp="1al1pbkej3lq0">• File management</span>
                      <span data-id="element-553" data-mp="1amcunonmh1j8">• Settings control</span>
                      <span data-id="element-554" data-mp="1fijs0t1yd4pk">• Cross-device sync</span>
                    </div>
                  </div>
                </button>

                {/* Companion App */}
                <button onClick={() => scrollToSection('mobile-app')} className="group bg-white/[0.05] backdrop-blur-[40px] rounded-[32px] p-8 border-2 border-white/20 shadow-[0_20px_60px_-20px_rgba(196,113,237,0.4),0_0_0_1px_rgba(196,113,237,0.1)] transition-all duration-400 hover:-translate-y-3 hover:scale-[1.05] hover:border-[#c471ed]/40 hover:shadow-[0_30px_80px_-20px_rgba(196,113,237,0.6),0_0_40px_rgba(196,113,237,0.3)] relative overflow-hidden cursor-pointer" data-id="element-555" data-mp="1phj1ueygf6e5">
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(196,113,237,0.15),transparent_60%)] opacity-0 group-hover:opacity-100 transition-opacity duration-400" data-id="element-556" data-mp="19vee0fyhgnv2"></div>
                  <div className="absolute -inset-[1px] bg-gradient-to-b from-[#c471ed]/20 to-transparent rounded-[32px] opacity-0 group-hover:opacity-100 transition-opacity duration-400" data-id="element-557" data-mp="i35jhhm8e5l4"></div>
                  <div className="relative flex flex-col items-center text-center" data-id="element-558" data-mp="qwsdb26voyro">
                    <div className="w-16 h-16 rounded-[20px] bg-gradient-to-br from-[#c471ed]/20 to-[#c471ed]/5 flex items-center justify-center mb-5 shadow-[inset_0_0_20px_rgba(196,113,237,0.1)] border border-[#c471ed]/20 group-hover:shadow-[0_0_30px_rgba(196,113,237,0.4)] transition-all duration-400" data-id="element-559" data-mp="m5sflupxqco4">
                      <SmartphoneIcon size={28} className="text-[#c471ed] group-hover:scale-110 transition-transform duration-400" data-id="element-560" data-mp="3gg3dipdnvcun" />
                    </div>
                    <h3 className="text-xl font-bold mb-2 tracking-tight text-white" data-id="element-561" data-mp="36iozsxj4h261">
                      Ari Mobile
                    </h3>
                    <p className="text-xs uppercase tracking-[1.5px] font-semibold text-white/60 mb-4" data-id="element-562" data-mp="3bee2h9qfuz6k">
                      iOS & Android Controller
                    </p>
                    <div className="text-sm text-white/70 leading-relaxed flex items-center justify-center gap-2 flex-wrap" data-id="element-563" data-mp="7zi63d0ogrtn">
                      <span data-id="element-564" data-mp="3pahrmomgghc6">• Device control</span>
                      <span data-id="element-565" data-mp="3u66yld0t10ii">• Chat with AI</span>
                      <span data-id="element-566" data-mp="3u91i92bzuhav">• Media management</span>
                    </div>
                  </div>
                </button>

                {/* Supported Devices */}
                <button onClick={() => scrollToSection('ari-os')} className="group bg-white/[0.05] backdrop-blur-[40px] rounded-[32px] p-8 border-2 border-white/20 shadow-[0_20px_60px_-20px_rgba(233,161,101,0.4),0_0_0_1px_rgba(233,161,101,0.1)] transition-all duration-400 hover:-translate-y-3 hover:scale-[1.05] hover:border-[#E9A165]/40 hover:shadow-[0_30px_80px_-20px_rgba(233,161,101,0.6),0_0_40px_rgba(233,161,101,0.3)] relative overflow-hidden cursor-pointer" data-id="element-567" data-mp="2itmkhwbynlep">
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(233,161,101,0.15),transparent_60%)] opacity-0 group-hover:opacity-100 transition-opacity duration-400" data-id="element-568" data-mp="3hvzb5yrhxvzp"></div>
                  <div className="absolute -inset-[1px] bg-gradient-to-b from-[#E9A165]/20 to-transparent rounded-[32px] opacity-0 group-hover:opacity-100 transition-opacity duration-400" data-id="element-569" data-mp="381yy9apz3mgf"></div>
                  <div className="relative flex flex-col items-center text-center" data-id="element-570" data-mp="1wimhrk79y10w">
                    <div className="w-16 h-16 rounded-[20px] bg-gradient-to-br from-[#E9A165]/20 to-[#E9A165]/5 flex items-center justify-center mb-5 shadow-[inset_0_0_20px_rgba(233,161,101,0.1)] border border-[#E9A165]/20 group-hover:shadow-[0_0_30px_rgba(233,161,101,0.4)] transition-all duration-400" data-id="element-571" data-mp="1rk2yxsnz95cr">
                      <LayersIcon size={28} className="text-[#E9A165] group-hover:scale-110 transition-transform duration-400" data-id="element-572" data-mp="h0gjruxmttvr" />
                    </div>
                    <h3 className="text-xl font-bold mb-2 tracking-tight text-white" data-id="element-573" data-mp="ut65dy990wsc">
                      Supported Devices
                    </h3>
                    <p className="text-xs uppercase tracking-[1.5px] font-semibold text-white/60 mb-4" data-id="element-574" data-mp="ztkg5o28iku2">
                      Smart Device Ecosystem
                    </p>
                    <div className="flex flex-wrap gap-2 justify-center mt-2" data-id="element-575" data-mp="31k5kenvr04sz">
                      <div className="bg-white/[0.08] px-3.5 py-2 rounded-full text-xs font-medium flex items-center gap-1.5 border border-white/10 hover:border-[#E9A165]/30 hover:bg-[#E9A165]/10 transition-all text-white" data-id="element-576" data-mp="36ip6qpe97ilv">
                        <GlassesIcon size={14} className="opacity-70 text-[#E9A165]" data-id="element-577" data-mp="2uh7is23u1e13" />
                        RealWear
                      </div>
                      <div className="bg-white/[0.08] px-3.5 py-2 rounded-full text-xs font-medium flex items-center gap-1.5 border border-white/10 hover:border-[#E9A165]/30 hover:bg-[#E9A165]/10 transition-all text-white" data-id="element-578" data-mp="33gcbuswmkf5v">
                        <GlassesIcon size={14} className="opacity-70 text-[#E9A165]" data-id="element-579" data-mp="3dd7arcwt8hko" />
                        Rokid
                      </div>
                      <div className="bg-white/[0.08] px-3.5 py-2 rounded-full text-xs font-medium flex items-center gap-1.5 border border-white/10 hover:border-[#E9A165]/30 hover:bg-[#E9A165]/10 transition-all text-white" data-id="element-580" data-mp="4zhy5g0do53w">
                        <GlassesIcon size={14} className="opacity-70 text-[#E9A165]" data-id="element-581" data-mp="jlxf2fbwzdem" />
                        Vuzix
                      </div>
                      <div className="bg-white/[0.08] px-3.5 py-2 rounded-full text-xs font-medium flex items-center gap-1.5 border border-white/10 hover:border-[#E9A165]/30 hover:bg-[#E9A165]/10 transition-all text-white" data-id="element-582" data-mp="9n7uufwjpgv7">
                        <GlassesIcon size={14} className="opacity-70 text-[#E9A165]" data-id="element-583" data-mp="3w2aplovivcdl" />
                        Others
                      </div>
                    </div>
                  </div>
                </button>
              </div>

              {/* Bottom Row - 2 Cards */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-32 relative z-10" data-id="element-584" data-mp="3d4gcaynd2epe">
                {/* Ari User Cloud */}
                <button onClick={() => scrollToSection('cloud')} className="group bg-white/[0.05] backdrop-blur-[40px] rounded-[32px] p-8 border-2 border-white/20 shadow-[0_20px_60px_-20px_rgba(246,79,89,0.4),0_0_0_1px_rgba(246,79,89,0.1)] transition-all duration-400 hover:-translate-y-3 hover:scale-[1.05] hover:border-[#f64f59]/40 hover:shadow-[0_30px_80px_-20px_rgba(246,79,89,0.6),0_0_40px_rgba(246,79,89,0.3)] relative overflow-hidden cursor-pointer" data-id="element-585" data-mp="3i31ciy2m4ydu">
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(246,79,89,0.15),transparent_60%)] opacity-0 group-hover:opacity-100 transition-opacity duration-400" data-id="element-586" data-mp="3ep2sf9kelcmg"></div>
                  <div className="absolute -inset-[1px] bg-gradient-to-b from-[#f64f59]/20 to-transparent rounded-[32px] opacity-0 group-hover:opacity-100 transition-opacity duration-400" data-id="element-587" data-mp="35sk2853k42tk"></div>
                  <div className="relative flex flex-col items-center text-center" data-id="element-588" data-mp="2vvp2xjzglye8">
                    <div className="w-16 h-16 rounded-[20px] bg-gradient-to-br from-[#f64f59]/20 to-[#f64f59]/5 flex items-center justify-center mb-5 shadow-[inset_0_0_20px_rgba(246,79,89,0.1)] border border-[#f64f59]/20 group-hover:shadow-[0_0_30px_rgba(246,79,89,0.4)] transition-all duration-400" data-id="element-589" data-mp="2qxxfb25ukyfu">
                      <CloudIcon size={28} className="text-[#f64f59] group-hover:scale-110 transition-transform duration-400" data-id="element-590" data-mp="2qs6615etdfcz" />
                    </div>
                    <h3 className="text-xl font-bold mb-2 tracking-tight text-white" data-id="element-591" data-mp="1y0uzbcafh4ln">
                      Ari Cloud
                    </h3>
                    <p className="text-xs uppercase tracking-[1.5px] font-semibold text-white/60 mb-4" data-id="element-592" data-mp="280vhuw0h3gos">
                      Personal Hub
                    </p>
                    <div className="text-sm text-white/70 leading-relaxed flex items-center justify-center gap-2 flex-wrap" data-id="element-593" data-mp="2ctoqhuz2b965">
                      <span data-id="element-594" data-mp="1kb15sgnz9w5l">• AI memory & context</span>
                      <span data-id="element-595" data-mp="1k9gc0t9hmh8s">• Files & documents</span>
                      <span data-id="element-596" data-mp="skvnle0xmxvr">• Photos & videos</span>
                      <span data-id="element-597" data-mp="sj2hq0y58hot">• Device settings</span>
                      <span data-id="element-598" data-mp="xj6vvkbkx0bv">• Chat history</span>
                    </div>
                  </div>
                </button>

                {/* Business Cloud */}
                <button onClick={() => scrollToSection('business')} className="group bg-white/[0.05] backdrop-blur-[40px] rounded-[32px] p-8 border-2 border-white/20 shadow-[0_20px_60px_-20px_rgba(246,79,89,0.4),0_0_0_1px_rgba(246,79,89,0.1)] transition-all duration-400 hover:-translate-y-3 hover:scale-[1.05] hover:border-[#f64f59]/40 hover:shadow-[0_30px_80px_-20px_rgba(246,79,89,0.6),0_0_40px_rgba(246,79,89,0.3)] relative overflow-hidden cursor-pointer" data-id="element-599" data-mp="nm267aiiymqb">
                  <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(246,79,89,0.15),transparent_60%)] opacity-0 group-hover:opacity-100 transition-opacity duration-400" data-id="element-600" data-mp="3n0nqrdxtb9gg"></div>
                  <div className="absolute -inset-[1px] bg-gradient-to-b from-[#f64f59]/20 to-transparent rounded-[32px] opacity-0 group-hover:opacity-100 transition-opacity duration-400" data-id="element-601" data-mp="38e79zkmd6vt6"></div>
                  <div className="relative flex flex-col items-center text-center" data-id="element-602" data-mp="3hex0q9wrpw01">
                    <div className="w-16 h-16 rounded-[20px] bg-gradient-to-br from-[#f64f59]/20 to-[#f64f59]/5 flex items-center justify-center mb-5 shadow-[inset_0_0_20px_rgba(246,79,89,0.1)] border border-[#f64f59]/20 group-hover:shadow-[0_0_30px_rgba(246,79,89,0.4)] transition-all duration-400" data-id="element-603" data-mp="3hcsp15smnsp7">
                      <BuildingIcon size={28} className="text-[#f64f59] group-hover:scale-110 transition-transform duration-400" data-id="element-604" data-mp="4091et4gyvyv" />
                    </div>
                    <h3 className="text-xl font-bold mb-2 tracking-tight text-white" data-id="element-605" data-mp="dxwu7diiltee">
                      Ari Business
                    </h3>
                    <p className="text-xs uppercase tracking-[1.5px] font-semibold text-white/60 mb-4" data-id="element-606" data-mp="916i119tzhy9">
                      Enterprise Control Hub
                    </p>
                    <div className="text-sm text-white/70 leading-relaxed flex flex-col items-center justify-center gap-1" data-id="element-607" data-mp="36tq3kjsl6w0e">
                      <div className="flex items-center justify-center gap-2 flex-wrap" data-id="element-608" data-mp="3bqowx04feyxj">
                        <span data-id="element-609" data-mp="3gkaxvv189edi">• Fleet management</span>
                        <span data-id="element-610" data-mp="3licgyfynldoz">• Mass deployment</span>
                      </div>
                      <div className="flex items-center justify-center gap-2 flex-wrap" data-id="element-611" data-mp="3qgw2wgd8yphc">
                        <span data-id="element-612" data-mp="3qiqs46vjx3r8">• Analytics</span>
                        <span data-id="element-613" data-mp="3ujkh73kqcolp">• Security policies</span>
                      </div>
                    </div>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Developers Section */}
        <section id="developers" className="min-h-screen flex items-center py-32 px-6 bg-gradient-to-b from-black to-[#0a0a0a]" data-id="element-614" data-mp="253l112ip1ywk">
          <div className="max-w-7xl mx-auto" data-id="element-615" data-mp="1xlmriy0iintc">
            <div className="text-center mb-16" data-id="element-616" data-mp="1stllvuxunvca">
              <div className="inline-block mb-6" data-id="element-617" data-mp="1srr06eer7z75">
                <div className="px-6 py-2 bg-gradient-to-r from-[#E9A165]/20 via-[#8846DE]/20 to-[#D25796]/20 backdrop-blur-xl border border-white/10 rounded-full" data-id="element-618" data-mp="1ntz73h2di71d">
                  <span className="text-sm font-semibold bg-gradient-to-r from-[#E9A165] via-[#8846DE] to-[#D25796] bg-clip-text text-transparent" data-id="element-619" data-mp="1it2tucw4t8ja">
                    DEVELOPERS
                  </span>
                </div>
              </div>
              <h2 className="text-5xl md:text-6xl font-bold text-white mb-6" data-id="element-620" data-mp="26k73zhfhc194">
                Build on Ari OS
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto" data-id="element-621" data-mp="22id8roo1zpgc">
                Comprehensive APIs, SDKs, and tools to extend the AriOS
                ecosystem
              </p>
            </div>

            {/* Developer Tools Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-20" data-id="element-622" data-mp="2f0o0t850jzzv">
              <a href="https://developer.ari-os.com/device_docs/docs/developer-guide" target="_blank" rel="noopener noreferrer" className="group relative hover:scale-105 transition-transform duration-300" data-id="element-623" data-mp="2eyur1jz3sznn">
                <div className="relative bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-xl rounded-2xl p-8 border border-white/10 transition-all h-full" data-id="element-624" data-mp="29zi6j5vpg4o8">
                  <div className="w-14 h-14 bg-gradient-to-br from-[#E9A165] via-[#8846DE] to-[#D25796] rounded-xl flex items-center justify-center mb-6 shadow-xl" data-id="element-625" data-mp="32p7a9qzsuitj">
                    <CodeIcon size={28} className="text-white" data-id="element-626" data-mp="2srssno253nzn" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3" data-id="element-627" data-mp="1dbbuvz0d8c0g">
                    REST APIs
                  </h3>
                  <p className="text-gray-300 mb-4 leading-relaxed" data-id="element-628" data-mp="1hdfa8yq5o0r0">
                    Comprehensive REST APIs for device control, cloud storage,
                    and AI features
                  </p>
                  <div className="text-[#8846DE] font-semibold flex items-center space-x-2" data-id="element-629" data-mp="14v35ydvdvc7p">
                    <span data-id="element-630" data-mp="1js3xohdkr766">API Docs</span>
                    <ArrowRightIcon size={16} className="group-hover:translate-x-1 transition-transform" data-id="element-631" data-mp="1jr2d9mao58bs" />
                  </div>
                </div>
              </a>

              <a href="https://developer.ari-os.com/device_docs/docs/developer-guide" target="_blank" rel="noopener noreferrer" className="group relative hover:scale-105 transition-transform duration-300" data-id="element-632" data-mp="m3tw23hpalfl">
                <div className="relative bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-xl rounded-2xl p-8 border border-white/10 transition-all h-full" data-id="element-633" data-mp="w0594vp47fb3">
                  <div className="w-14 h-14 bg-gradient-to-br from-[#E9A165] via-[#8846DE] to-[#D25796] rounded-xl flex items-center justify-center mb-6 shadow-xl" data-id="element-634" data-mp="3cj7vu3d0mre">
                    <PackageIcon size={28} className="text-white" data-id="element-635" data-mp="x2jl7m47zye7" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3" data-id="element-636" data-mp="11u1owhxmod2v">SDKs</h3>
                  <p className="text-gray-300 mb-4 leading-relaxed" data-id="element-637" data-mp="11w4bst3hhq3d">
                    Native SDKs for iOS, Android, Web, and AR device development
                  </p>
                  <div className="text-[#8846DE] font-semibold flex items-center space-x-2" data-id="element-638" data-mp="a46nvo0eqyyd">
                    <span data-id="element-639" data-mp="e4pgvle1eb25">Download SDKs</span>
                    <ArrowRightIcon size={16} className="group-hover:translate-x-1 transition-transform" data-id="element-640" data-mp="j3a6yrzkrecv" />
                  </div>
                </div>
              </a>

              <a href="https://github.com/realwear" target="_blank" rel="noopener noreferrer" className="group relative hover:scale-105 transition-transform duration-300" data-id="element-641" data-mp="1uhdylriowqer">
                <div className="relative bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-xl rounded-2xl p-8 border border-white/10 transition-all h-full" data-id="element-642" data-mp="3k1f6awk8rxzk">
                  <div className="w-14 h-14 bg-gradient-to-br from-[#E9A165] via-[#8846DE] to-[#D25796] rounded-xl flex items-center justify-center mb-6 shadow-xl" data-id="element-643" data-mp="3a8ggjbd0e1ed">
                    <GitBranchIcon size={28} className="text-white" data-id="element-644" data-mp="319mgtc4dsuwl" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3" data-id="element-645" data-mp="2w9gumbgvs0lw">GitHub</h3>
                  <p className="text-gray-300 mb-4 leading-relaxed" data-id="element-646" data-mp="2rax746mn2grf">
                    Open-source examples, sample apps, and community
                    contributions
                  </p>
                  <div className="text-[#8846DE] font-semibold flex items-center space-x-2" data-id="element-647" data-mp="2me6tdpyb3x96">
                    <span data-id="element-648" data-mp="ph6anip79esk">View Repos</span>
                    <ArrowRightIcon size={16} className="group-hover:translate-x-1 transition-transform" data-id="element-649" data-mp="kk7jn90u1nyl" />
                  </div>
                </div>
              </a>

              <a href="https://developer.ari-os.com/device_docs/docs/developer-guide" target="_blank" rel="noopener noreferrer" className="group relative hover:scale-105 transition-transform duration-300" data-id="element-650" data-mp="3uj2gl2nr14eb">
                <div className="relative bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-xl rounded-2xl p-8 border border-white/10 transition-all h-full" data-id="element-651" data-mp="36t8970m7881g">
                  <div className="w-14 h-14 bg-gradient-to-br from-[#E9A165] via-[#8846DE] to-[#D25796] rounded-xl flex items-center justify-center mb-6 shadow-xl" data-id="element-652" data-mp="2xttiq1617xgy">
                    <BookOpenIcon size={28} className="text-white" data-id="element-653" data-mp="31wznt5herilu" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3" data-id="element-654" data-mp="vzu8lj4nbm5u">
                    Documentation
                  </h3>
                  <p className="text-gray-300 mb-4 leading-relaxed" data-id="element-655" data-mp="2klknm5udz8j6">
                    Comprehensive guides, tutorials, and best practices
                  </p>
                  <div className="text-[#8846DE] font-semibold flex items-center space-x-2" data-id="element-656" data-mp="2gltzk9w8v6ic">
                    <span data-id="element-657" data-mp="2ucyr0opgkshl">Read Docs</span>
                    <ArrowRightIcon size={16} className="group-hover:translate-x-1 transition-transform" data-id="element-658" data-mp="2pknpjhf2kymx" />
                  </div>
                </div>
              </a>

              <a href="https://developer.ari-os.com/device_docs/docs/developer-guide" target="_blank" rel="noopener noreferrer" className="group relative hover:scale-105 transition-transform duration-300" data-id="element-659" data-mp="2z9fgbm8e9cw9">
                <div className="relative bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-xl rounded-2xl p-8 border border-white/10 transition-all h-full" data-id="element-660" data-mp="1ivfhyfp75vb4">
                  <div className="w-14 h-14 bg-gradient-to-br from-[#E9A165] via-[#8846DE] to-[#D25796] rounded-xl flex items-center justify-center mb-6 shadow-xl" data-id="element-661" data-mp="1dzievb2y2lga">
                    <TerminalIcon size={28} className="text-white" data-id="element-662" data-mp="1qsxxls21lbhk" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3" data-id="element-663" data-mp="25h14d3nwsr7f">
                    CLI Tools
                  </h3>
                  <p className="text-gray-300 mb-4 leading-relaxed" data-id="element-664" data-mp="20o7xoa8vr8sp">
                    Command-line tools for development, testing, and deployment
                  </p>
                  <div className="text-[#8846DE] font-semibold flex items-center space-x-2" data-id="element-665" data-mp="2aekim3klx3zd">
                    <span data-id="element-666" data-mp="2oe2dn0ohxdh6">Install CLI</span>
                    <ArrowRightIcon size={16} className="group-hover:translate-x-1 transition-transform" data-id="element-667" data-mp="2ocr92yn881p0" />
                  </div>
                </div>
              </a>

              <a href="https://discord.com/invite/mkG5eVXrkS" target="_blank" rel="noopener noreferrer" className="group relative hover:scale-105 transition-transform duration-300" data-id="element-668" data-mp="p2j1kd7wbp3a">
                <div className="relative bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-xl rounded-2xl p-8 border border-white/10 transition-all h-full" data-id="element-669" data-mp="35qilrvx8ltxc">
                  <div className="w-14 h-14 bg-gradient-to-br from-[#E9A165] via-[#8846DE] to-[#D25796] rounded-xl flex items-center justify-center mb-6 shadow-xl" data-id="element-670" data-mp="2vxblgphct444">
                    <UsersIcon size={28} className="text-white" data-id="element-671" data-mp="2mxogjtpf1g6b" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-3" data-id="element-672" data-mp="280p700b07d97">
                    Community
                  </h3>
                  <p className="text-gray-300 mb-4 leading-relaxed" data-id="element-673" data-mp="2cyz085d643s6">
                    Join our developer community, forums, and Discord server
                  </p>
                  <div className="text-[#8846DE] font-semibold flex items-center space-x-2" data-id="element-674" data-mp="b8f6cth6z7k2">
                    <span data-id="element-675" data-mp="b61cup25u3em">Join Community</span>
                    <ArrowRightIcon size={16} className="group-hover:translate-x-1 transition-transform" data-id="element-676" data-mp="9s9pnodg0xp5" />
                  </div>
                </div>
              </a>
            </div>

            {/* Code Example */}
            <div className="relative" data-id="element-677" data-mp="3rxd3bxhh6i16"></div>

            {/* OEM CTA */}
            <div className="mt-20 text-center" data-id="element-678" data-mp="shq4lgpvksp3">
              <h3 className="text-3xl font-bold text-white mb-4" data-id="element-679" data-mp="sgeqamyt02jq">
                AR/AI Device Manufacturer?
              </h3>
              <p className="text-gray-300 mb-8 max-w-2xl mx-auto text-lg" data-id="element-680" data-mp="nkpzb80untz1">
                Join the Ari OS ecosystem and bring AI-powered voice control to
                your devices
              </p>
              <button onClick={() => setContactModalOpen(true)} className="inline-block px-10 py-4 bg-gradient-to-r from-[#E9A165] via-[#8846DE] to-[#D25796] text-white rounded-xl hover:shadow-2xl hover:shadow-[#8846DE]/50 transition-all font-bold text-lg" data-id="element-681" data-mp="eo8k9tpd7by0">
                Partner With Us
              </button>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-32 px-6" data-id="element-682" data-mp="2y4fh8yulfjl3">
          <div className="max-w-5xl mx-auto text-center" data-id="element-683" data-mp="2y3du3w0az6n0">
            <div className="relative" data-id="element-684" data-mp="334jody9m31yn">
              <div className="absolute -inset-8 bg-gradient-to-r from-[#E9A165] via-[#8846DE] to-[#D25796] rounded-3xl opacity-20 blur-3xl" data-id="element-685" data-mp="335l96ugfj2tk"></div>
              <div className="relative bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-xl rounded-3xl p-16 border-2 border-white/10" data-id="element-686" data-mp="3d1y2hr82bens">
                <h2 className="text-5xl md:text-6xl font-bold text-white mb-6" data-id="element-687" data-mp="3hu7o4v259sk9">
                  Ready to Experience
                  <br data-id="element-688" data-mp="3ltq73laentkg" />
                  the Future?
                </h2>
                <p className="text-xl text-gray-300 mb-12 max-w-2xl mx-auto" data-id="element-689" data-mp="3quxh7r00t4ua">
                  Join thousands of users already working hands-free with AriOS
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-690" data-mp="3i5704jp26y0c">
                  <a href="https://chat.ari-os.com" target="_blank" rel="noopener noreferrer" className="px-12 py-6 bg-gradient-to-r from-[#E9A165] via-[#8846DE] to-[#D25796] text-white rounded-xl hover:shadow-2xl hover:shadow-[#8846DE]/50 transition-all font-bold text-xl group relative overflow-hidden" data-id="element-691" data-mp="3d7fa5v7z5lys">
                    <span className="relative z-10" data-id="element-692" data-mp="2u8t7wd0q1ota">Get Started Free</span>
                    <div className="absolute inset-0 bg-gradient-to-r from-[#D25796] via-[#8846DE] to-[#E9A165] opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-693" data-mp="395aekqxyuf9i"></div>
                  </a>
                  <button onClick={() => scrollToSection('architecture')} className="px-12 py-6 bg-white/5 backdrop-blur-xl border-2 border-white/20 text-white rounded-xl hover:bg-white/10 hover:border-white/40 transition-all font-bold text-xl" data-id="element-694" data-mp="of1lp8aqgp82">
                    Ecosystem Architecture
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <SiteFooter data-id="element-695" data-mp="i0dzo8giunk1" />
      </div>

      {/* Contact Modal */}
      {contactModalOpen && <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" data-id="element-696" data-mp="m0wpxy2rnlk4">
          <div className="relative w-full max-w-lg bg-gradient-to-br from-zinc-900 to-zinc-950 rounded-2xl border-2 border-white/10 shadow-2xl" data-id="element-697" data-mp="19qqza5nvmrzm">
            {/* Close button */}
            <button onClick={() => setContactModalOpen(false)} className="absolute top-4 right-4 p-2 text-white/60 hover:text-white hover:bg-white/10 rounded-lg transition-colors" data-id="element-698" data-mp="10wvsyub0qvie">
              <XIcon size={24} data-id="element-699" data-mp="1smql0yq9gzar" />
            </button>

            <div className="p-10 text-center" data-id="element-700" data-mp="1xftktolmt4ze">
              {/* Icon */}
              <div className="w-16 h-16 bg-gradient-to-br from-[#E9A165] via-[#8846DE] to-[#D25796] rounded-2xl flex items-center justify-center mx-auto mb-6" data-id="element-701" data-mp="27ba3wag4ywj4">
                <MessageSquareIcon size={32} className="text-white" data-id="element-702" data-mp="23aq6r6vkbswz" />
              </div>

              <h3 className="text-3xl font-bold text-white mb-4" data-id="element-703" data-mp="1y9annkcy3wh8">
                Partner With Ari OS
              </h3>

              <p className="text-gray-300 text-lg leading-relaxed mb-6" data-id="element-704" data-mp="1te53apz6ypv9">
                Interested in bringing Ari OS to your AR or AI device? We'd love
                to hear from you. Send us an email describing your device and
                use case, and we'll happily set up a meeting to explore the
                partnership.
              </p>

              {/* Email */}
              <div className="bg-white/5 border border-white/10 rounded-xl p-5 mb-6" data-id="element-705" data-mp="1an44q31zgwc8">
                <p className="text-zinc-400 text-sm mb-2" data-id="element-706" data-mp="15lol8ffgexvy">
                  Send your inquiry to
                </p>
                <a href="mailto:ingrid.maes@realwear.com" className="text-xl font-semibold bg-gradient-to-r from-[#E9A165] via-[#8846DE] to-[#D25796] bg-clip-text text-transparent hover:opacity-80 transition-opacity" data-id="element-707" data-mp="10p6itm7ljbms">
                  ingrid.maes@realwear.com
                </a>
              </div>

              {/* What to include */}
              <div className="text-left" data-id="element-708" data-mp="15fnx9r8tsu2p">
                <p className="text-zinc-400 text-sm mb-3" data-id="element-709" data-mp="7sx80svhlz8m">
                  Please include in your email:
                </p>
                <div className="space-y-2" data-id="element-710" data-mp="cstf50zhe6se">
                  <div className="flex items-start space-x-3" data-id="element-711" data-mp="cpfcdrmwnvkf">
                    <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-712" data-mp="hq34yhqs8kgr">
                      <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-713" data-mp="mjnzu0x2wybq" />
                    </div>
                    <p className="text-zinc-400 text-sm" data-id="element-714" data-mp="3l5qxprhystl0">
                      Your company and role
                    </p>
                  </div>
                  <div className="flex items-start space-x-3" data-id="element-715" data-mp="188s5duiapjhe">
                    <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-716" data-mp="1465blo22dohk">
                      <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-717" data-mp="1i66ipl8x9s5h" />
                    </div>
                    <p className="text-zinc-400 text-sm" data-id="element-718" data-mp="1d5sb24b8gk7z">
                      A brief description of your device
                    </p>
                  </div>
                  <div className="flex items-start space-x-3" data-id="element-719" data-mp="1n4zn8o68e8ti">
                    <div className="w-5 h-5 rounded-full bg-[#8846DE]/20 flex items-center justify-center flex-shrink-0 mt-0.5" data-id="element-720" data-mp="1n4r9yv6751hs">
                      <CheckCircleIcon size={12} className="text-[#8846DE]" data-id="element-721" data-mp="1vxbci6plzq7w" />
                    </div>
                    <p className="text-zinc-400 text-sm" data-id="element-722" data-mp="2aulodh65yy45">
                      How you'd like to integrate with Ari OS
                    </p>
                  </div>
                </div>
              </div>

              {/* CTA Button */}
            </div>
          </div>
        </div>}

      <style data-id="element-723" data-mp="2tiw3vd88y5oy">{`
        /* Apple-inspired smooth animations */
        @keyframes gradient {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        
        @keyframes pulse-slow {
          0%, 100% { opacity: 0.3; transform: scale(1); }
          50% { opacity: 0.5; transform: scale(1.05); }
        }
        
        @keyframes grid-flow {
          0% { transform: translateY(0); }
          100% { transform: translateY(100px); }
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-20px); }
        }
        
        @keyframes shimmer {
          0% { background-position: -1000px 0; }
          100% { background-position: 1000px 0; }
        }
        
        @keyframes glow-pulse {
          0%, 100% { box-shadow: 0 0 20px rgba(136, 70, 222, 0.3); }
          50% { box-shadow: 0 0 40px rgba(136, 70, 222, 0.6), 0 0 60px rgba(233, 161, 101, 0.4); }
        }
        
        .animate-gradient {
          background-size: 200% 200%;
          animation: gradient 15s ease infinite;
        }
        
        .animate-pulse-slow {
          animation: pulse-slow 8s ease-in-out infinite;
        }
        
        .animate-grid-flow {
          animation: grid-flow 20s linear infinite;
        }
        
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        
        .animate-shimmer {
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent);
          background-size: 1000px 100%;
          animation: shimmer 3s infinite;
        }
        
        .animate-glow-pulse {
          animation: glow-pulse 3s ease-in-out infinite;
        }
        
        /* Glass morphism effects */
        .glass-card {
          background: rgba(255, 255, 255, 0.03);
          backdrop-filter: blur(20px) saturate(180%);
          -webkit-backdrop-filter: blur(20px) saturate(180%);
          border: 1px solid rgba(255, 255, 255, 0.1);
          box-shadow: 
            0 8px 32px 0 rgba(0, 0, 0, 0.37),
            inset 0 1px 0 0 rgba(255, 255, 255, 0.05);
        }
        
        .glass-card:hover {
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.15);
          box-shadow: 
            0 12px 48px 0 rgba(0, 0, 0, 0.5),
            inset 0 1px 0 0 rgba(255, 255, 255, 0.1),
            0 0 0 1px rgba(136, 70, 222, 0.2);
        }
        
        /* Smooth scroll */
        html {
          scroll-behavior: smooth;
        }
        
      `}</style>
    </div>;
}